import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaFilePdf } from 'react-icons/fa';
import { calculateSeminarScore } from '../../utils/seminarUtils';
import { generateBeritaAcaraNilaiAkhir, getNilaiMutu } from '../../utils/pdfGenerator';

// Proporsi bobot nilai akhir gabungan
const BOBOT = { proposal: 0.30, hasil: 0.40, kompre: 0.30 };

/**
 * Modal rekap nilai akhir gabungan untuk satu mahasiswa/tugas akhir.
 * Menampilkan nilai Seminar Proposal (30%), Seminar Hasil (40%),
 * Sidang Komprehensif (30%), nilai akhir, dan nilai mutu, serta
 * tombol untuk mengunduh Berita Acara Nilai Akhir.
 */
const RekapNilaiAkhir = ({ group, show, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tugasAkhir, setTugasAkhir] = useState(null);
  const [evaluators, setEvaluators] = useState([]);
  const [scores, setScores] = useState({ proposal: null, hasil: null, kompre: null });
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    if (group && show) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, show]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Ambil data tugas akhir
      let taData = null;
      if (group.tugasAkhirId) {
        const taDoc = await getDoc(doc(db, 'tugasAkhir', group.tugasAkhirId));
        if (taDoc.exists()) {
          taData = taDoc.data();
          setTugasAkhir(taData);
        }
      }

      // Hitung nilai tiap tahap menggunakan util standar
      const newScores = { proposal: null, hasil: null, kompre: null };
      ['proposal', 'hasil', 'kompre'].forEach((jenis) => {
        const sem = group[jenis];
        if (sem && sem.nilai && Object.keys(sem.nilai).length > 0) {
          const result = calculateSeminarScore(sem, taData);
          newScores[jenis] = result.finalScore;
        }
      });
      setScores(newScores);

      // Ambil evaluators (pembimbing + penguji) dengan tanda tangan.
      // Gunakan seminar paling lengkap sebagai acuan komite: kompre > hasil > proposal
      const refSeminar = group.kompre || group.hasil || group.proposal;
      if (refSeminar && Array.isArray(refSeminar.pengujiId)) {
        const pembimbing1Id = taData?.pembimbingId?.[0];
        const pembimbing2Id = taData?.pembimbingId?.[1];
        const nonPembimbingIds = refSeminar.pengujiId.filter(
          (id) => id !== pembimbing1Id && id !== pembimbing2Id
        );

        const evaluatorData = [];
        for (let i = 0; i < refSeminar.pengujiId.length; i++) {
          const pengujiId = refSeminar.pengujiId[i];
          const pengujiDoc = await getDoc(doc(db, 'users', pengujiId));
          if (!pengujiDoc.exists()) continue;
          const pengujiData = pengujiDoc.data();

          let role;
          if (pengujiId === pembimbing1Id) role = 'Pembimbing 1';
          else if (pengujiId === pembimbing2Id) role = 'Pembimbing 2';
          else if (nonPembimbingIds[0] === pengujiId) role = 'Penguji 1';
          else if (nonPembimbingIds[1] === pengujiId) role = 'Penguji 2';
          else role = 'Penguji';

          evaluatorData.push({
            id: pengujiId,
            nama: pengujiData.nama || refSeminar.pengujiNama?.[i] || `Penguji ${i + 1}`,
            role,
            nip: pengujiData.nip || '-',
            signatureURL: pengujiData.signatureURL || null
          });
        }

        evaluatorData.sort((a, b) => {
          const order = { 'Pembimbing 1': 0, 'Pembimbing 2': 1, 'Penguji 1': 2, 'Penguji 2': 3 };
          return (order[a.role] ?? 9) - (order[b.role] ?? 9);
        });

        setEvaluators(evaluatorData);
      }
    } catch (err) {
      console.error('Error fetching rekap nilai:', err);
      setError('Terjadi kesalahan saat mengambil data rekap nilai');
    } finally {
      setLoading(false);
    }
  };

  const nilaiAkhir =
    (Number(scores.proposal) || 0) * BOBOT.proposal +
    (Number(scores.hasil) || 0) * BOBOT.hasil +
    (Number(scores.kompre) || 0) * BOBOT.kompre;

  const nilaiMutu = getNilaiMutu(nilaiAkhir);
  const semuaLengkap =
    scores.proposal !== null && scores.hasil !== null && scores.kompre !== null;

  const handleGenerate = async () => {
    try {
      setGeneratingPdf(true);
      setError('');

      const refSeminar = group.kompre || group.hasil || group.proposal;
      const pdfBlob = await generateBeritaAcaraNilaiAkhir({
        seminar: {
          ...refSeminar,
          tugasAkhirId: group.tugasAkhirId,
          mahasiswaNama: group.mahasiswaNama,
          mahasiswaNIM: group.mahasiswaNIM,
          judulTA: group.judulTA,
          // Tanggal berita acara mengikuti tanggal Sidang Komprehensif
          tanggal: group.kompre?.tanggal || refSeminar.tanggal
        },
        tugasAkhir,
        evaluators,
        komponenNilai: {
          proposal: scores.proposal,
          hasil: scores.hasil,
          kompre: scores.kompre
        }
      });

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Berita_Acara_Nilai_Akhir_${group.mahasiswaNIM || 'Mahasiswa'}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating berita acara nilai akhir:', err);
      setError('Terjadi kesalahan saat membuat Berita Acara Nilai Akhir');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const fmt = (v) => (v === null || v === undefined || isNaN(Number(v)) ? '-' : Number(v).toFixed(2));

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Rekap Nilai Akhir Tugas Akhir</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <Alert variant="info">Memuat data...</Alert>
        ) : (
          <>
            <p className="mb-1"><strong>Mahasiswa:</strong> {group.mahasiswaNama || '-'} ({group.mahasiswaNIM || '-'})</p>
            <p className="mb-3"><strong>Judul:</strong> {tugasAkhir?.judul || group.judulTA || '-'}</p>

            <Table responsive striped bordered>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Komponen Penilaian</th>
                  <th className="text-center">Nilai</th>
                  <th className="text-center">Bobot</th>
                  <th className="text-center">Nilai Terbobot</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Seminar Proposal</td>
                  <td className="text-center">{fmt(scores.proposal)}</td>
                  <td className="text-center">30%</td>
                  <td className="text-center">{fmt((Number(scores.proposal) || 0) * BOBOT.proposal)}</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>Seminar Hasil</td>
                  <td className="text-center">{fmt(scores.hasil)}</td>
                  <td className="text-center">40%</td>
                  <td className="text-center">{fmt((Number(scores.hasil) || 0) * BOBOT.hasil)}</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>Sidang Komprehensif</td>
                  <td className="text-center">{fmt(scores.kompre)}</td>
                  <td className="text-center">30%</td>
                  <td className="text-center">{fmt((Number(scores.kompre) || 0) * BOBOT.kompre)}</td>
                </tr>
                <tr className="table-primary">
                  <td colSpan={4}><strong>Nilai Akhir</strong></td>
                  <td className="text-center"><strong>{nilaiAkhir.toFixed(2)}</strong></td>
                </tr>
                <tr className="table-primary">
                  <td colSpan={4}><strong>Nilai Mutu</strong></td>
                  <td className="text-center"><strong>{nilaiMutu}</strong></td>
                </tr>
              </tbody>
            </Table>

            <div className="mb-3">
              <Badge bg={nilaiAkhir >= 60 ? 'success' : 'danger'}>
                {nilaiAkhir >= 60 ? 'LULUS' : 'TIDAK LULUS'}
              </Badge>
              {!semuaLengkap && (
                <Badge bg="warning" className="ms-2">
                  Nilai belum lengkap
                </Badge>
              )}
            </div>

            {!semuaLengkap && (
              <Alert variant="warning" className="py-2">
                Sebagian tahapan belum memiliki nilai. Nilai akhir dihitung dengan
                menganggap tahapan yang belum dinilai bernilai 0.
              </Alert>
            )}

            <Alert variant="info" className="py-2 mb-0">
              <strong>Penandatangan:</strong>{' '}
              {evaluators.length > 0
                ? evaluators.map((e) => `${e.nama} (${e.role})`).join(', ')
                : 'Data pembimbing/penguji tidak ditemukan'}
              , diketahui oleh Koordinator Tugas Akhir.
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={generatingPdf || loading}
        >
          {generatingPdf ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Memproses...
            </>
          ) : (
            <>
              <FaFilePdf className="me-2" /> Unduh Berita Acara Nilai Akhir
            </>
          )}
        </Button>
        <Button variant="secondary" onClick={onHide}>Tutup</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RekapNilaiAkhir;
